import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ScopedAuditTable, type AuditRow } from '../../components/scoped/ScopedAuditTable';
import { TENANT_DESCRIPTOR } from '../../components/scoped/scopeDescriptors';
import { tenantApi } from '../../api/endpoints/tenant';
import { useAuth } from '../../hooks/useAuth';
import { shortId } from '../../lib/shortId';

function TenantAuditPage() {
    const { user } = useAuth();
    const tid = user?.tenantId ?? '';

    // El audit log NO guarda el nombre del actor (solo actorCognitoSub). Resolvemos el nombre con la
    // lista de usuarios del tenant (best-effort): si el actor es staff de plataforma o no está en la
    // lista, cae al cognitoSub. pageSize alto para cubrir el padrón del tenant en una página.
    const { data: usersData } = useQuery({
        queryKey: ['tenant', tid, 'users', 'audit-actor-map'],
        queryFn: () => tenantApi.listUsers(tid, { pageSize: 200 }),
        enabled: !!tid,
    });
    const actorNameBySub = useMemo(() => {
        const map = new Map<string, string>();
        for (const u of usersData?.items ?? []) map.set(u.cognitoSub, u.displayName);
        return map;
    }, [usersData]);

    // Mapa para resolver el Target a nombres legibles (en vez del UUID): roles del tenant.
    const { data: rolesData } = useQuery({
        queryKey: ['tenant', tid, 'roles', 'audit-target-map'],
        queryFn: () => tenantApi.listRoles(tid),
        enabled: !!tid,
    });
    const roleNameById = useMemo(() => {
        const map = new Map<string, string>();
        for (const r of rolesData?.items ?? []) map.set(r.roleId, r.roleName);
        return map;
    }, [rolesData]);

    const resolveActor = useCallback(
        (row: AuditRow): string => {
            const sub = row.actor?.cognitoSub;
            return (
                (sub && actorNameBySub.get(sub)) || row.actor?.email || (sub ? shortId(sub) : '—')
            );
        },
        [actorNameBySub],
    );

    // Target legible: resuelve el id de la entidad al nombre según su tipo. NUNCA emite el UUID crudo:
    // los tipos sin listado propio caen a un id acortado etiquetado, igual que los tipos desconocidos.
    const resolveTarget = useCallback(
        (row: AuditRow): string => {
            const type = row.target?.type ?? '';
            const id = row.target?.id ?? '';
            if (!type && !id) return '—';
            if (type === 'TenantUser') return `Usuario: ${actorNameBySub.get(id) ?? shortId(id)}`;
            if (type === 'Role') return `Rol: ${roleNameById.get(id) ?? shortId(id)}`;
            return `${type}: ${shortId(id)}`;
        },
        [actorNameBySub, roleNameById],
    );

    return (
        <ScopedAuditTable
            descriptor={TENANT_DESCRIPTOR}
            resolveActor={resolveActor}
            resolveTarget={resolveTarget}
        />
    );
}

export const Route = createFileRoute('/_authenticated/_layout/tenant/audit')({
    component: TenantAuditPage,
});
